// Pure client-side filtering + descriptive stats for the Safety Dashboard.
// The backend serves UNFILTERED corridor payloads once; every control in the
// FilterBar re-slices them here (useMemo'd in the page) — no refetch on filter
// change. Keep these functions pure and cheap: they run on every filter click
// over at most a few hundred crashes and a few hundred runs per corridor.
//
// Multi-corridor: runs, crashes, sites and clusters carry a `corridor` tag
// ("route|pathId") so mileposts of different routes never get compared.

import { kabcoOf, visibleFindings } from './insightsText';
import { clusterScore, crashScore } from './offenders';

export const RUN_ANALYSES = ['SSD', 'PSD', 'GLR', 'FSA', 'HCA', 'GRW', 'ISD'];

/** How each analysis' deficiency is correlated to a crash (the Settings
 *  dialog lists these; a disabled rule drops that analysis' findings
 *  everywhere — Insights, popups, correlated-only, cluster insights). */
export const CORRELATION_RULES = [
  { key: 'SSD', title: 'SSD — stopping sight distance',
    rule: 'The crash lies within the 0.02 mi station gate of an SSD-deficient station (speed_difference < 0, anchored at the sight-target point one stopping distance downstream of the observer). Animal strikes correlate with a case-dependent caveat and stay out of the cost figures.' },
  { key: 'PSD', title: 'PSD — passing sight distance',
    rule: 'The crash lies within the station gate of an Unsafe pass station (the marking allows passing where the passing sight distance is not available) AND is the type a failed or aborted passing manoeuvre produces: head-on / front-to-front, opposite-direction sideswipe, or a record that names passing/overtaking. Same-direction sideswipes, rear-ends and single-vehicle crashes do not count.' },
  { key: 'GLR', title: 'GLR — sun glare',
    rule: 'The crash date, time and travel direction fall inside the location\'s sun-glare impact window (±3 days, ±30 minutes, direction-matched) at a station with ≥ 9,600 glare-minutes per year. Animal strikes correlate with a caveat (glare delays detection).' },
  { key: 'FSA', title: 'FSA — flat spot analysis',
    rule: 'The crash lies within the station gate of a flat spot (gradient, the resultant of the cross and long slope, at or below the 0.5% threshold: poor drainage) AND happened on a wet, icy, snowy or slushy surface.' },
  { key: 'HCA', title: 'HCA — horizontal curves',
    rule: 'The crash lies inside (± station gate) a curve whose safety score is below 100% — its safe speed is under the posted speed.' },
  { key: 'GRW', title: 'GRW — guardrail warrant',
    rule: 'The crash lies within the station gate of a station where a guardrail is warranted (slope_calc.GRW = YES). A guardrail strike there counts as MITIGATED: the existing guardrail validates the warrant and no new installation is priced.' },
  { key: 'ISD', title: 'ISD — intersection sight distance',
    rule: 'A non-animal crash lies within the intersection\'s required AASHTO intersection sight distance (worst leg), grouped by physical intersection.' },
];

export const DEFAULT_FILTERS = {
  direction: 'both',        // 'P' | 'N' | 'both'
  analyses: ['SSD'],        // subset of RUN_ANALYSES; [] = all (the 'ALL' view)
  correlatedOnly: false,    // keep only crashes WITH findings + runs near them
  showCrashes: true,        // false = RDV analyses only (crash layers/tables hidden)
  mpRange: null,            // [min, max] or null = full corridor (single corridor only)
  years: [],                // [] = all crash years; else e.g. ['2024','2026']
  severities: [],           // [] = all; subset of fatal|injury|pdo|unknown
  manners: [],              // [] = all
  lights: [],
  surfaces: [],
  excludeAnimal: false,
  wetOnly: false,           // FSA mechanism: wet/snow/ice surface crashes only
  glareOnly: false,         // GLR mechanism: crash time inside a glare period
  embankmentOnly: false,    // GRW mechanism: embankment / overturn crashes
  passingOnly: false,       // PSD mechanism: head-on / opposite-direction crashes
  dateRange: null,          // [isoFrom, isoTo] or null
  // CMF countermeasure layer (cmfBenefit.js)
  showCmf: true,            // countermeasure + benefit columns/sections on
  cmfPolicy: '45',          // '45' = 4-5★ with 3★ fallback | '5' | '3'
  discountRate: 7,          // % — present value of the service-life benefit
  disabledRules: [],        // correlation rules switched off in Settings (analysis keys)
  aboveChanceOnly: false,   // keep only runs/findings of analyses whose verdict is Above chance
};

export const sameCorridor = (a, b) => (a?.corridor || '') === (b?.corridor || '');

/** The single analysis in scope, or 'ALL' when several / none are selected. */
export const analysisOf = (filters) =>
  (filters.analyses && filters.analyses.length === 1 ? filters.analyses[0] : 'ALL');
/** Analyses actually shown (selection, or every run analysis). */
export const selectedAnalyses = (filters) =>
  (filters.analyses && filters.analyses.length ? filters.analyses : RUN_ANALYSES);

/** Nearest-run distance for a crash under the current analysis selection —
 *  'ALL' takes the minimum across every analysis with runs. */
export function nearestRunFor(crash, analysis) {
  const m = crash.nearestRunMi || {};
  if (analysis !== 'ALL') return m[analysis] ?? null;
  const vals = Object.values(m).filter((v) => v !== null && v !== undefined);
  return vals.length ? Math.min(...vals) : null;
}

const inMp = (mp, range) => !range || (mp >= range[0] && mp <= range[1]);
const inSet = (value, set) => !set.length || set.includes(value);

/** Drop the findings of disabled correlation rules (Settings dialog). */
export function applyDisabledRules(crash, disabledRules) {
  if (!disabledRules?.length || !crash.findings) return crash;
  const findings = {};
  for (const [k, v] of Object.entries(crash.findings)) {
    if (!disabledRules.includes(k)) findings[k] = v;
  }
  return { ...crash, findings };
}

export function filterCrashes(crashes, filters) {
  if (!crashes) return [];
  const [from, to] = filters.dateRange || [null, null];
  // findings outside the selected analyses behave like disabled rules
  const shown = selectedAnalyses(filters);
  const hidden = [...(filters.disabledRules || []),
    ...(shown.length < RUN_ANALYSES.length ? RUN_ANALYSES.filter((a) => !shown.includes(a)) : [])];
  const out = [];
  for (const c of crashes) {
    if (filters.direction !== 'both' && c.direction !== filters.direction) continue;
    if (!inMp(c.milepost, filters.mpRange)) continue;
    if (filters.years.length && !filters.years.includes(String(c.year))) continue;
    if (!inSet(c.severityClass, filters.severities)) continue;
    if (!inSet(c.manner, filters.manners)) continue;
    if (!inSet(c.light, filters.lights)) continue;
    if (!inSet(c.surface, filters.surfaces)) continue;
    if (filters.excludeAnimal && c.isAnimal) continue;
    if (filters.wetOnly && !c.isWetSurface) continue;
    if (filters.glareOnly && !c.glareMatch) continue;
    if (filters.embankmentOnly && !c.isEmbankment) continue;
    if (filters.passingOnly && !c.isPassingRelated) continue;
    if (from && (!c.datetime || c.datetime < from)) continue;
    if (to && (!c.datetime || c.datetime > to)) continue;
    out.push(applyDisabledRules(c, hidden));
  }
  return out;
}

/** `runs` is the flat list of runs already tagged with direction/corridor. */
export function filterRuns(runs, filters) {
  const out = [];
  for (const run of runs || []) {
    if (filters.direction !== 'both' && run.direction !== filters.direction) continue;
    if (filters.mpRange && !(run.mpEnd >= filters.mpRange[0] && run.mpStart <= filters.mpRange[1])) continue;
    out.push(run);
  }
  return out.sort((a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.mpStart - b.mpStart);
}

/** Distinct values (sorted, non-empty) of one crash attribute — feeds the
 *  manner/light/surface multi-selects so options always match the data. */
export function distinctValues(crashes, key) {
  const vals = [...new Set((crashes || []).map((c) => c[key]).filter(Boolean).map(String))];
  return key === 'year' ? vals.sort((a, b) => a - b) : vals.sort();
}

const NEAR_MI = 0.1; // "near a deficiency" bucket edge, ~528 ft (SR-2 study buffer)

export function computeDescriptiveStats(crashes, runs, corridor, analysis) {
  const lengthMi = corridor?.lengthMi || null;
  const runMi = runs.reduce((s, r) => s + (r.lengthMi || 0), 0);
  const bySeverity = { fatal: 0, injury: 0, pdo: 0, unknown: 0 };
  let animal = 0;
  let near = 0;
  let inside = 0;
  let glareMatched = 0;
  let embankment = 0;
  let wet = 0;
  let passing = 0;
  const nearestVisibleRun = (c) => {
    let best = null;
    for (const r of runs) {
      if (!sameCorridor(r, c)) continue;
      if (c.milepost >= r.mpStart && c.milepost <= r.mpEnd) return 0;
      const d = Math.min(Math.abs(c.milepost - r.mpStart), Math.abs(c.milepost - r.mpEnd));
      if (best === null || d < best) best = d;
    }
    return best;
  };
  for (const c of crashes) {
    bySeverity[c.severityClass] = (bySeverity[c.severityClass] || 0) + 1;
    if (c.isAnimal) animal += 1;
    if (c.glareMatch) glareMatched += 1;
    if (c.isEmbankment) embankment += 1;
    if (c.isWetSurface) wet += 1;
    if (c.isPassingRelated) passing += 1;
    // distance against the runs actually on screen (direction/MP filters
    // included) — not the precomputed both-direction nearestRunMi
    const d = runs.length ? nearestVisibleRun(c) : nearestRunFor(c, analysis);
    if (d === 0) inside += 1;
    if (d !== undefined && d !== null && d <= NEAR_MI) near += 1;
  }
  return {
    nCrashes: crashes.length,
    nAnimal: animal,
    nGlareMatched: glareMatched,
    nEmbankment: embankment,
    nWetSurface: wet,
    nPassing: passing,
    bySeverity,
    corridorMiles: lengthMi ? +lengthMi.toFixed(2) : null,
    crashesPerMile: lengthMi ? +(crashes.length / lengthMi).toFixed(2) : null,
    runCount: runs.length,
    runMiles: +runMi.toFixed(2),
    runShareOfCorridor: lengthMi ? +((runMi / lengthMi) * 100).toFixed(1) : null,
    crashesInsideRuns: inside,
    crashesNearRuns: near,
    nearShare: crashes.length ? +((near / crashes.length) * 100).toFixed(0) : null,
    nearBufferMi: NEAR_MI,
  };
}

/** "Correlated only" — SAME definition the Insights section uses, so the map,
 *  tables and Insights always agree on the numbers: a correlated crash is one
 *  with >=1 deficiency FINDING at its location under the current analysis
 *  scope; kept runs are those with a correlated non-animal crash within the
 *  table buffer. Applied AFTER the ordinary filters. */
export function applyCorrelatedOnly(runs, crashes, analysis, bufferMi = NEAR_MI) {
  const keptCrashes = crashes.filter((c) => visibleFindings(c, analysis).length > 0);
  const keptRuns = runs.filter((r) =>
    keptCrashes.some((c) => sameCorridor(r, c)
      && c.milepost >= r.mpStart - bufferMi && c.milepost <= r.mpEnd + bufferMi));
  return { runs: keptRuns, crashes: keptCrashes };
}

/** Cluster the VISIBLE crashes (>=minCount within gapMi, per corridor) so the
 *  cluster rings and table always match the current filters. */
export function clusterVisibleCrashes(crashes, gapMi = 0.3, minCount = 2) {
  const rel = [...(crashes || [])]
    .sort((a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.milepost - b.milepost);
  const groups = [];
  let cur = [];
  for (const c of rel) {
    const last = cur[cur.length - 1];
    if (cur.length && (!sameCorridor(last, c) || c.milepost - last.milepost > gapMi)) {
      if (cur.length >= minCount) groups.push(cur);
      cur = [];
    }
    cur.push(c);
  }
  if (cur.length >= minCount) groups.push(cur);
  return groups.map((g) => ({
    corridor: g[0].corridor,
    road: g[0].road,
    mpStart: g[0].milepost,
    mpEnd: g[g.length - 1].milepost,
    mpCenter: +((g[0].milepost + g[g.length - 1].milepost) / 2).toFixed(4),
    lat: g[Math.floor(g.length / 2)].lat,
    lng: g[Math.floor(g.length / 2)].lon,
    count: g.length,
    crashIds: g.map((c) => c.id),
  }));
}

/** Per-run site rollup: crashes within ±bufferMi of each run (same corridor). */
export function computeSiteTable(runs, crashes, bufferMi = NEAR_MI) {
  return runs.map((run) => {
    const hits = crashes.filter(
      (c) => sameCorridor(run, c)
        && c.milepost >= run.mpStart - bufferMi && c.milepost <= run.mpEnd + bufferMi
    );
    const nonAnimal = hits.filter((c) => !c.isAnimal);
    return {
      ...run,
      crashCount: hits.length,
      nonAnimalCount: nonAnimal.length,
      injuryCount: nonAnimal.filter((c) => c.severityClass === 'fatal' || c.severityClass === 'injury').length,
    };
  });
}

const SEVERITY_WEIGHT = { severe: 3, moderate: 2, minor: 1 };

/** Deficiency clusters — stretches where SEVERAL deficiency runs sit together
 *  (any direction, same corridor, gaps ≤ gapMi). Works with or without crash
 *  data: the crash fields are simply empty when no crashes are given. */
export function clusterDeficiencies(runs, crashes, opts = {}) {
  const { gapMi = NEAR_MI, costs = {}, minRuns = 2 } = opts;
  const sorted = [...(runs || [])].sort(
    (a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.mpStart - b.mpStart);
  const groups = [];
  let cur = [];
  let curEnd = -Infinity;
  for (const r of sorted) {
    const last = cur[cur.length - 1];
    if (cur.length && (!sameCorridor(last, r) || r.mpStart > curEnd + gapMi)) {
      if (cur.length >= minRuns) groups.push(cur);
      cur = [];
      curEnd = -Infinity;
    }
    cur.push(r);
    curEnd = Math.max(curEnd, r.mpEnd);
  }
  if (cur.length >= minRuns) groups.push(cur);

  return groups.map((g, i) => {
    const mpStart = Math.min(...g.map((r) => r.mpStart));
    const mpEnd = Math.max(...g.map((r) => r.mpEnd));
    const analyses = {};
    let severityScore = 0;
    const severityCounts = { severe: 0, moderate: 0, minor: 0 };     // runs by their worst severity
    for (const r of g) {
      severityCounts[severityCounts[r.worstSeverity] !== undefined ? r.worstSeverity : 'minor'] += 1;
      const a = analyses[r.analysis] || (analyses[r.analysis] = { runs: 0, worstValue: null, worstSeverity: 'minor', directions: new Set(), reasons: new Set() });
      a.runs += 1;
      (r.reasons || []).forEach((x) => a.reasons.add(x));        // PSD under the rules: why each run counts
      a.directions.add(r.direction);
      if (a.worstValue === null || (SEVERITY_WEIGHT[r.worstSeverity] || 0) > (SEVERITY_WEIGHT[a.worstSeverity] || 0)) {
        a.worstValue = r.worstValue;
        a.worstSeverity = r.worstSeverity;
        a.curveNumber = r.curveNumber;
      }
      severityScore += SEVERITY_WEIGHT[r.worstSeverity] || 1;
    }
    for (const a of Object.values(analyses)) { a.directions = [...a.directions].sort(); a.reasons = [...a.reasons]; }
    const members = (crashes || []).filter((c) => sameCorridor(g[0], c)
      && c.milepost >= mpStart - gapMi && c.milepost <= mpEnd + gapMi);
    const nonAnimal = members.filter((c) => !c.isAnimal);
    const keys = Object.keys(analyses);
    const correlated = members.filter((c) => Object.keys(c.findings || {}).some((k) => keys.includes(k)));
    const mix = { K: 0, A: 0, B: 0, C: 0, O: 0 };
    let cost = 0;
    for (const c of members) {
      const k = kabcoOf(c);
      mix[k] += 1;
      cost += costs[k] || 0;
    }
    const coords = g.flatMap((r) => (r.coords || []).filter((p) => p && p[0] != null));
    const mid = coords[Math.floor(coords.length / 2)] || [null, null];
    return {
      id: `dc-${g[0].corridor || ''}-${i}`,
      corridor: g[0].corridor,
      road: g[0].road,
      mpStart, mpEnd,
      lengthMi: +(mpEnd - mpStart).toFixed(3),
      lat: mid[0], lng: mid[1],
      bounds: coords,
      runs: g,
      runKeys: g.map((r) => `${r.corridor || ''}|${r.analysis}-${r.direction}-${r.id}`),
      analyses,
      analysisCount: keys.length,
      analysisKeys: keys,
      runCount: g.length,
      severityScore,
      severityCounts,
      crashes: members,
      crashCount: members.length,
      nonAnimalCount: nonAnimal.length,
      correlatedCount: correlated.length,
      fatalCount: mix.K,
      injuryCount: mix.A + mix.B + mix.C,
      mix, cost,
      // "worst offender" score: crash cost dominates when crash data exists;
      // otherwise the number and severity of overlapping deficiencies
      worstScore: cost + severityScore * 10000 + keys.length * 50000 + correlated.length * 25000,
    };
  });
}

/** Sort helpers shared by the Insights and Cluster-insights navigators. */
export const INSIGHT_SORTS = [
  { key: 'mp', label: 'Milepost' },
  { key: 'worst', label: 'Worst offenders' },
];
export function sortCrashesForInsights(crashes, sortBy, costs = {}, weights = null) {
  const arr = [...crashes];
  if (sortBy === 'worst' && weights) {
    return arr.sort((a, b) => crashScore(b, costs, weights) - crashScore(a, costs, weights) || a.milepost - b.milepost);
  }
  if (sortBy === 'worst') {
    return arr.sort((a, b) => ((costs[kabcoOf(b)] || 0) - (costs[kabcoOf(a)] || 0))
      || (Object.keys(b.findings || {}).length - Object.keys(a.findings || {}).length)
      || a.milepost - b.milepost);
  }
  return arr.sort((a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.milepost - b.milepost);
}
export function sortClusters(clusters, sortBy, weights = null) {
  const arr = [...clusters];
  if (sortBy === 'worst' && weights) return arr.sort((a, b) => clusterScore(b, weights) - clusterScore(a, weights) || a.mpStart - b.mpStart);
  if (sortBy === 'worst') return arr.sort((a, b) => b.worstScore - a.worstScore || a.mpStart - b.mpStart);
  return arr.sort((a, b) => (a.corridor || '').localeCompare(b.corridor || '') || a.mpStart - b.mpStart);
}

const EPDO_WEIGHTS = { K: 21, A: 21, B: 21, C: 21, O: 1 };
const JUNCTION_TYPES = [
  ['four-way', 'Four-way'], ['t-intersection', 'T-intersection'], ['y-intersection', 'Y-intersection'],
  ['five-point', 'Five-point'], ['traffic circle', 'Roundabout'], ['roundabout', 'Roundabout'],
];
export function junctionType(descr) {
  const t = (descr || '').toLowerCase();
  const hit = JUNCTION_TYPES.find(([tok]) => t.includes(tok));
  return hit ? hit[1] : null;
}
const IX_MERGE_M = 50;

/** Intersections along a corridor derived from the crash records themselves
 *  (MassDOT junction type), merged with the RDV ISD intersections when the
 *  corridor has them. Junction crashes within 50 m form one intersection —
 *  the same scale the Intersection priorities clustering uses. */
export function deriveIntersections(crashes, rdvIntersections = []) {
  const points = (rdvIntersections || []).map((ix) => ({ ...ix, source: 'rdv', types: {} }));
  const dist = (a, b) => {
    const dy = (a.lat - b.lat) * 111320;
    const dx = ((a.lng ?? a.lon) - (b.lng ?? b.lon)) * 111320 * Math.cos((a.lat * Math.PI) / 180);
    return Math.sqrt(dx * dx + dy * dy);
  };
  for (const c of crashes || []) {
    const type = junctionType(c.junction);
    if (!type || c.lat == null) continue;
    let host = points.find((p) => (p.corridor || '') === (c.corridor || '') && dist(p, c) <= IX_MERGE_M);
    if (!host) {
      host = { id: `J-${(c.corridor || '')}-${points.length + 1}`, corridor: c.corridor, road: c.road,
        lat: c.lat, lng: c.lon, mp: c.milepost, source: 'crash', n: 0, types: {}, mpSum: 0 };
      points.push(host);
    }
    if (host.source === 'crash') {
      host.n += 1;
      host.mpSum += c.milepost;
      host.mp = +(host.mpSum / host.n).toFixed(4);
    }
    host.types[type] = (host.types[type] || 0) + 1;
  }
  return points.map((p) => {
    const byCount = Object.entries(p.types).sort((a, b) => b[1] - a[1]);
    const legs = (p.legs || []).length;
    const type = byCount[0]?.[0] || (legs >= 4 ? 'Four-way' : legs === 3 ? 'T-intersection' : 'Intersection');
    return { ...p, type };
  });
}
const INTERSECTION_RADIUS_M = 76; // ~250 ft, same as the intersection priorities clustering scale

/** Crash statistics per RDV intersection (for the strip's intersection row):
 *  crashes within ~250 ft of the intersection point, K/A/B/C/O mix, cost and
 *  MassDOT tempered EPDO — the same figures the Intersection priorities table
 *  shows. Type from the ISD leg count (4+ legs = four-way, 3 = T). */
export function intersectionStats(intersections, crashes, costs = {}) {
  return (intersections || []).map((ix) => {
    const near = (crashes || []).filter((c) => {
      const dy = (c.lat - ix.lat) * 111320;
      const dx = (c.lon - ix.lng) * 111320 * Math.cos((ix.lat * Math.PI) / 180);
      return Math.sqrt(dx * dx + dy * dy) <= INTERSECTION_RADIUS_M;
    });
    const mix = { K: 0, A: 0, B: 0, C: 0, O: 0 };
    let cost = 0;
    let epdo = 0;
    for (const c of near) {
      const k = kabcoOf(c);
      mix[k] += 1;
      cost += costs[k] || 0;
      epdo += EPDO_WEIGHTS[k];
    }
    const legs = (ix.legs || []).length;
    const type = ix.type || (legs >= 4 ? 'Four-way' : legs === 3 ? 'T-intersection' : 'Intersection');
    return { ...ix, type, legsCount: legs, crashes: near.length,
      nonAnimal: near.filter((c) => !c.isAnimal).length, mix, cost, epdo, crashList: near };
  });
}
